import PropTypes from 'prop-types';
import React from 'react';

import { Swiper, SwiperSlide } from 'swiper/react';
import { IconArrowRight} from '$partials/icons';
import { useCarousel } from '$contexts/carousel.jsx';

import Button from './button';
import IconArrowLeft from '$partials/icons/arrow-left';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @component Carousel
 * @props (see propTypes)
 */
const Carousel = ({ children, className }) => {
    const { swiperConfig } = useCarousel();

    if (!swiperConfig) { return null; }

    return (
        <Swiper
            {...swiperConfig}
            className={className}>
            {children}
        </Swiper>
    );
};

/**
 * @component CarouselNavigationLeft
 * @props (see propTypes)
 */
const CarouselNavigationLeft = () => {
    const { swiper, isPrevDisabled } = useCarousel();

    if (!swiper) { return null; }

    return (
        <Button
            aria-label='Navigate left'
            className='items-center bg-orange-transition text-white px-15 py-15 rounded-l-button'
            disabled={isPrevDisabled}
            onClick={() => swiper?.slidePrev()}
            renderIcon={ <IconArrowLeft className='h-13' />}
            type='button' />
    );
};

/**
 * @component CarouselNavigationRight
 * @props (see propTypes)
 */
const CarouselNavigationRight = () => {
    const { swiper, isNextDisabled } = useCarousel();

    if (!swiper) { return null; }

    return (
        <Button
            aria-label='Next right'
            className='items-center bg-orange-transition text-white px-15 py-15 rounded-r-button'
            disabled={isNextDisabled}
            onClick={() => swiper?.slideNext()}
            renderIcon={ <IconArrowRight className='h-13' />}
            type='button' />
    );
};

/**
 * @component CarouselNavigation
 * @props (see propTypes)
 */
const CarouselNavigation = ({ className }) => {
    const { swiper } = useCarousel();

    if (!swiper) { return null; }

    return (
        <div className={`flex ${className ? className : ''}`}>
            <CarouselNavigationLeft />
            <CarouselNavigationRight />
        </div>
    );
};

/**
 * @component CarouselPagination
 * @props (see propTypes)
 */
const CarouselPagination = ({ className, pages }) => {
    const { swiper } = useCarousel();

    if (!swiper) { return null; }
    return (
        <div className={`${className ? className : ''}`}>
            {swiper.slides.map((slide, idx) => {
                return (
                    <div
                        className={`dot mr-15 cursor-pointer${pages[idx]?.backgroundColor ? 'bg-white' : ' bg-peach'}${swiper?.realIndex === idx ? ' active' : ''}`}
                        key={`swiper-pagination-${idx}`}
                        label={`Go to slide ${idx}`}
                        onClick={(e) => {
                            e.preventDefault();
                            swiper?.slideTo(idx);
                        }}/>
                );
            })}
        </div>
    );
};

/**
 * @component CarouselSlide
 * @props (see propTypes)
 */
const CarouselSlide = SwiperSlide;

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

Carousel.propTypes = {
    children: PropTypes.any,
    className: PropTypes.string
};

CarouselNavigation.propTypes = {
    className: PropTypes.string
};

CarouselNavigationLeft.propTypes = {
};

CarouselNavigationRight.propTypes = {
};

CarouselPagination.propTypes = {
    className: PropTypes.string,
    pages: PropTypes.array,
};

CarouselSlide.propTypes = {
    children: PropTypes.any,
    className: PropTypes.string,
    index: PropTypes.number
};

export {
    Carousel,
    CarouselNavigation,
    CarouselNavigationLeft,
    CarouselNavigationRight,
    CarouselPagination,
    CarouselSlide
};

export default Carousel;
